import { ReactElement } from 'react';

import { getSdk } from '@OnArk/sdk';
import { recordRequestEnd, recordRequestStart } from '@OnArk/utils/saveStatus';
import { GraphQLClient } from 'graphql-request';
import Cookies from 'universal-cookie';

// Infer the page's full prop type (`Props`) directly from the component rather
// than reconstructing it with an `[key: string]: any` index signature. The old
// signature `<R extends { [key: string]: any }>` used that `any` to let `R`
// absorb each page's arbitrary declared props during inference — removing the
// index signature naively collapses `R` to its constraint and breaks all
// callers. Inferring `Props` from the component keeps full type safety with no
// `any`: the wrapper then takes those same props minus `client` (which
// withClient injects), and `token`/`guestToken` are read from cookies here, not
// from props, so they don't belong in the constraint.
type WithClient = <Props extends { client: Sdk }>(
  PageComponent: (props: Props) => ReactElement
) => (pageProps: Omit<Props, 'client'>) => ReactElement;

const withClient: WithClient = PageComponent => {
  return pageProps => {
    const cookies = new Cookies();
    // universal-cookie's get() is typed `any`; cookie values are strings here.
    const token = cookies.get<string | undefined>('onark-token');
    const guestToken = cookies.get<string | undefined>('onark-guest-token');

    const client = new GraphQLClient(process.env.NEXT_PUBLIC_GRAPHQL_URI!, {
      // credentials: 'include' lets the browser send cookies on this
      // cross-origin POST. The API's CORS config (`credentials: true`)
      // and per-origin allowlist permit it. We do this so HttpOnly
      // auth cookies (which JS can't read) still authenticate the
      // request — falling back to the manual authorization header
      // only if a JS-readable token is also present.
      credentials: 'include',
      mode: 'cors',
      // Track every GraphQL request so the section editor can show
      // a "Saving…" / "Saved Xs ago" indicator. The store inside
      // saveStatus.ts ignores quick pings (< 200ms) to avoid making
      // the indicator flicker for ordinary background queries.
      requestMiddleware: req => {
        if (typeof window !== 'undefined') recordRequestStart();

        return req;
      },
      responseMiddleware: () => {
        if (typeof window !== 'undefined') recordRequestEnd();
      },
    });

    if (token) client.setHeader('authorization', token);
    if (guestToken) client.setHeader('authorization-guest', guestToken);
    if (typeof window !== 'undefined')
      client.setHeader('from-url', window.location.href);

    // Re-adding `client` to `Omit<Props, 'client'>` reconstructs the component's
    // prop type, but TS can't prove that identity for a generic, so assert it
    // via the component's own parameter type (the alias's `Props` param isn't
    // nameable in this impl). This is the single contained cast that lets every
    // *caller* stay fully type-checked with no `any`.
    return PageComponent({ ...pageProps, client: getSdk(client) } as Parameters<
      typeof PageComponent
    >[0]);
  };
};

export default withClient;
